import { Injectable, inject, signal, computed } from '@angular/core'
import { HttpClient, HttpContext } from '@angular/common/http'
import { Observable, BehaviorSubject, tap, catchError, of } from 'rxjs'
import { environment } from '@environments/environment'
import { BypassErrorHandling } from '@app/v2/shared/helpers/http-context-tokens'
import { WorkspacesApiResponse } from '../types/organization.type'
import { Workspace } from '@workspaces/interfaces/workspace.interface'
import { OrganizationSwitcherState, OrganizationSwitcherEvents } from '../enums/organization-switcher.enum'
import { AuthService } from '@app/modules/auth/services/auth.service'

@Injectable({
  providedIn: 'root'
})
export class OrganizationSwitcherService {
  private _http = inject(HttpClient)
  private _authService = inject(AuthService)
  // State management using signals
  private _organizations = signal<Workspace[]>([])
  private _currentOrganization = signal<Workspace | null>(null)
  private _state = signal<OrganizationSwitcherState>(OrganizationSwitcherState.IDLE)
  private _error = signal<string | null>(null)
  private _currentPage = signal<number>(1)
  private _hasMorePages = signal<boolean>(true)
  private _isLoadingMore = signal<boolean>(false)
  private _isSearching = signal<boolean>(false)
  private _searchQuery = signal<string>('')
  private _hasLoadedMore = signal<boolean>(false)

  // Computed values
  public organizations = computed(() => this._organizations())
  public currentOrganization = computed(() => this._currentOrganization())
  public state = computed(() => this._state())
  public error = computed(() => this._error())
  public isLoading = computed(() => this._state() === OrganizationSwitcherState.LOADING)
  public isSwitching = computed(() => this._state() === OrganizationSwitcherState.SWITCHING)
  public currentPage = computed(() => this._currentPage())
  public hasMorePages = computed(() => this._hasMorePages())
  public isLoadingMore = computed(() => this._isLoadingMore())
  public isSearching = computed(() => this._isSearching())
  public searchQuery = computed(() => this._searchQuery())
  public hasLoadedMore = computed(() => this._hasLoadedMore())

  // Event subject for external components to subscribe to
  private _events$ = new BehaviorSubject<{ type: OrganizationSwitcherEvents; data?: any }>({
    type: OrganizationSwitcherEvents.ORGANIZATION_LOADED
  })

  /**
   * Observable for organization switcher events
   */
  public events$ = this._events$.asObservable()

  /**
   * Loads all workspaces for the current user (resets pagination)
   * @param searchQuery - Optional search query
   * @returns Observable<WorkspacesApiResponse>
   */
  public loadOrganizations(searchQuery?: string): Observable<WorkspacesApiResponse> {
    const locatStorageOrganizations = this._authService.workspaces
    if (locatStorageOrganizations.length > 0 && searchQuery === undefined) {
      this._organizations.set(locatStorageOrganizations)
      this._state.set(OrganizationSwitcherState.LOADED)
      return of({
        data: locatStorageOrganizations,
        last_page: 1,
        total: locatStorageOrganizations.length
      } as WorkspacesApiResponse)
    }
    this._state.set(OrganizationSwitcherState.LOADING)
    this._error.set(null)
    this._currentPage.set(1)
    this._hasMorePages.set(true)
    this._organizations.set([])
    this._searchQuery.set(searchQuery || '')

    return this.loadOrganizationsPage(1, true, searchQuery)
  }

  /**
   * Loads a specific page of workspaces
   * @param page - Page number to load
   * @param reset - Whether to reset the organizations array
   * @param searchQuery - Optional search query parameter
   * @returns Observable<WorkspacesApiResponse>
   */
  public loadOrganizationsPage(page: number, reset: boolean = false, searchQuery?: string): Observable<WorkspacesApiResponse> {
    if (reset) {
      this._state.set(OrganizationSwitcherState.LOADING)
    } else {
      this._isLoadingMore.set(true)
    }
    this._error.set(null)

    // Build URL with search parameter if provided
    let url = `${environment.v2.baseUrl}plan/contactless/workspaces?page=${page}`
    if (searchQuery && searchQuery.trim().length >= 3) {
      url += `&search=${encodeURIComponent(searchQuery.trim())}`
    }

    return this._http
      .get<WorkspacesApiResponse>(url, {
        context: new HttpContext().set(BypassErrorHandling, true)
      })
      .pipe(
        tap((response) => {
          const organizations = response.data

          if (reset) {
            this._organizations.set(organizations)
            this._state.set(OrganizationSwitcherState.LOADED)
            // Set first organization as current if none is set
            if (organizations.length > 0 && !this._currentOrganization()) {
              this._currentOrganization.set(organizations[0])
            }
          } else {
            this._organizations.update((current) => [...current, ...organizations])
            this._isLoadingMore.set(false)
          }

          this._currentPage.set(page)
          this._hasMorePages.set(page < response.last_page)

          this._events$.next({
            type: OrganizationSwitcherEvents.ORGANIZATION_LOADED,
            data: { organizations, page, hasMore: page < response.last_page, total: response.total }
          })
        }),
        catchError((error) => {
          console.error(`Error loading organizations page ${page}:`, error)
          this._error.set(error.message || 'Failed to load organizations')
          this._state.set(OrganizationSwitcherState.ERROR)
          this._isLoadingMore.set(false)
          this._events$.next({ type: OrganizationSwitcherEvents.ORGANIZATION_ERROR, data: error })
          return of({
            data: [],
            last_page: page,
            total: 0
          } as WorkspacesApiResponse)
        })
      )
  }

  /**
   * Loads the next page of organizations for infinite scrolling
   * @returns Observable<WorkspacesApiResponse>
   */
  public loadMoreOrganizations(): Observable<WorkspacesApiResponse> {
    if (!this._hasMorePages() || this._isLoadingMore()) {
      return of({
        data: [],
        last_page: this._currentPage(),
        total: 0
      } as WorkspacesApiResponse)
    }

    const nextPage = this._currentPage() + 1
    this._hasLoadedMore.set(true) // Mark that we've loaded more data
    return this.loadOrganizationsPage(nextPage, false, this._searchQuery())
  }

  /**
   * Searches organizations with the given query
   * @param query - Search query (minimum 3 characters)
   * @returns Observable<WorkspacesApiResponse>
   */
  public searchOrganizations(query: string): Observable<WorkspacesApiResponse> {
    this._isSearching.set(true)
    this._error.set(null)

    if (query.trim().length < 3) {
      // If query is less than 3 characters, load all organizations
      this._isSearching.set(false)
      return this.loadOrganizations()
    }

    return this.loadOrganizations(query.trim()).pipe(
      tap(() => this._isSearching.set(false)),
      catchError((error) => {
        this._isSearching.set(false)
        this._error.set(error.message || 'Search failed')
        return of({
          data: [],
          last_page: 1,
          total: 0
        } as WorkspacesApiResponse)
      })
    )
  }

  /**
   * Switches to a different organization (local only - no API call)
   * @param organizationId - The ID of the organization to switch to
   * @returns Observable<boolean> - True if successful, false if organization not found
   */
  public switchOrganization(organizationId: number): Observable<boolean> {
    this._state.set(OrganizationSwitcherState.SWITCHING)
    this._error.set(null)

    // Find the organization in the current list
    const targetOrganization = this._organizations().find((org) => org.id === organizationId)

    if (!targetOrganization) {
      this._state.set(OrganizationSwitcherState.ERROR)
      this._error.set('Organization not found')
      this._events$.next({
        type: OrganizationSwitcherEvents.ORGANIZATION_ERROR,
        data: { message: 'Organization not found' }
      })
      return of(false)
    }

    // Update current organization locally (no API call)
    this._currentOrganization.set(targetOrganization)
    this._state.set(OrganizationSwitcherState.LOADED)

    // Emit success event
    this._events$.next({
      type: OrganizationSwitcherEvents.ORGANIZATION_SWITCHED,
      data: { organization: targetOrganization }
    })

    return of(true)
  }
}
